import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Savebar } from "@dashboard/components/Savebar";
import { getApiUrl, getAppPlatformUrl } from "@dashboard/config";
import { ExtensionsPaths } from "@dashboard/extensions/urls";
import { PermissionEnum, useAppCreateMutation } from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { Box, Text } from "@saleor/macaw-ui-next";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { TieredPricingModal, TierFormData } from "../components/TieredPricingModal";
import { getRedactedFields, postConfigurationUpdate } from "../services/configurationApi";
import { PriceTier, TieredPricingFormData } from "../types";

const API_BASE_URL = getAppPlatformUrl("app-configurations");

const TieredPricingView: React.FC = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const [createApp] = useAppCreateMutation();

  const [formData, setFormData] = useState<TieredPricingFormData>({
    tiers: [],
    isEnabled: false,
  });
  const [showModal, setShowModal] = useState(false);
  const [editingTier, setEditingTier] = useState<PriceTier | null>(null);
  const [saveButtonBarState, setSaveButtonBarState] =
    useState<ConfirmButtonTransitionState>("default");
  const [hasChanges, setHasChanges] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Create the Saleor app for tiered_pricing (first-time activation)
  const createTieredPricingApp = async (
    authTokenCallback: (token: string) => void,
  ): Promise<boolean> => {
    try {
      const result = await createApp({
        variables: {
          input: {
            name: "Tiered Pricing",
            permissions: [PermissionEnum.MANAGE_CHECKOUTS, PermissionEnum.HANDLE_CHECKOUTS],
          },
        },
      });

      if (result.data?.appCreate?.errors?.length === 0 && result.data?.appCreate?.authToken) {
        authTokenCallback(result.data.appCreate.authToken);

        return true;
      }

      console.error("Failed to create tiered pricing app:", result.data?.appCreate?.errors);

      return false;
    } catch (err) {
      console.error("Error creating tiered pricing app:", err);

      return false;
    }
  };

  // Load existing tiered pricing config from API
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const TENANT_URL = getApiUrl();

        if (!TENANT_URL) {
          setInitialLoading(false);

          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/app/get-configuration?tenant=${encodeURIComponent(TENANT_URL)}`,
          { method: "GET", headers: { "Content-Type": "application/json" } },
        );

        if (response.ok) {
          const result = await response.json();
          const tieredApp = Array.isArray(result)
            ? result.find((app: any) => app.app_name === "tiered_pricing")
            : null;

          if (tieredApp?.configurations) {
            const tiers: PriceTier[] = Object.entries(tieredApp.configurations)
              .filter(([key]) => key.startsWith("tier_"))
              .map(([key, config]: [string, any]) => ({
                id: key,
                name: config.name || key,
                minQuantity: config.min_quantity ?? 1,
                discountType: config.discount_type ?? "percentage",
                discountValue: config.discount_value ?? 0,
                isActive: config.is_active ?? true,
              }));

            setFormData({
              tiers,
              isEnabled: tieredApp.is_active !== false,
            });
          }
        }
      } catch (error) {
        console.error("Failed to load tiered pricing config:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    loadConfig();
  }, []);

  const handleSubmit = async () => {
    setSaveButtonBarState("loading");

    try {
      const TENANT_URL = getApiUrl();

      if (!TENANT_URL) {
        throw new Error("API_URL environment variable is not configured");
      }

      // Check if this is first activation (no token yet)
      const getResponse = await fetch(
        `${API_BASE_URL}/app/get-configuration?tenant=${encodeURIComponent(TENANT_URL)}`,
        { method: "GET", headers: { "Content-Type": "application/json" } },
      );

      let existingToken: string | null = null;
      let hasRedactedToken = false;

      if (getResponse.ok) {
        const existing = await getResponse.json();
        const tieredApp = Array.isArray(existing)
          ? existing.find((app: any) => app.app_name === "tiered_pricing")
          : null;

        existingToken = tieredApp?.configurations?.token ?? null;
        hasRedactedToken = getRedactedFields(tieredApp).includes("token");
      }

      let tokenToSave = existingToken;

      // If enabling for the first time and no token exists, create the Saleor app
      if (formData.isEnabled && !existingToken && !hasRedactedToken) {
        let newToken: string | null = null;
        const created = await createTieredPricingApp(token => {
          newToken = token;
        });

        if (!created || !newToken) {
          throw new Error("Failed to create Tiered Pricing app in Saleor");
        }

        tokenToSave = newToken;
      }

      // Build configurations object
      const configurations: Record<string, any> = {};

      if (tokenToSave) {
        configurations.token = tokenToSave;
      }

      formData.tiers.forEach(tier => {
        configurations[tier.id] = {
          name: tier.name,
          min_quantity: tier.minQuantity,
          discount_type: tier.discountType,
          discount_value: tier.discountValue,
          is_active: tier.isActive,
        };
      });

      const saveResponse = await postConfigurationUpdate({
        tenant: TENANT_URL,
        app_name: "tiered_pricing",
        configurations,
        is_active: formData.isEnabled,
      });

      if (!saveResponse.ok) {
        throw new Error(`Failed to save tiered pricing configuration: ${saveResponse.status}`);
      }

      setSaveButtonBarState("success");
      setHasChanges(false);
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges),
      });
    } catch (error) {
      console.error("Failed to save tiered pricing:", error);
      setSaveButtonBarState("error");
      notify({
        status: "error",
        text: intl.formatMessage(commonMessages.somethingWentWrong),
      });
    }
  };

  const updateFormData = (updates: Partial<TieredPricingFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const handleModalSave = (tierFormData: TierFormData) => {
    const existingNumbers = formData.tiers
      .map(t => {
        const match = t.id.match(/tier_(\d+)$/);

        return match ? parseInt(match[1], 10) : 0;
      })
      .filter(n => !isNaN(n));

    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    const tierId = editingTier ? editingTier.id : `tier_${nextNumber}`;

    const newTier: PriceTier = {
      id: tierId,
      name: tierFormData.name,
      minQuantity: parseInt(tierFormData.minQuantity, 10),
      discountType: tierFormData.discountType,
      discountValue: parseFloat(tierFormData.discountValue),
      isActive: tierFormData.isActive,
    };

    if (editingTier) {
      updateFormData({
        tiers: formData.tiers.map(t => (t.id === editingTier.id ? newTier : t)),
      });
    } else {
      updateFormData({ tiers: [...formData.tiers, newTier] });
    }

    setShowModal(false);
    setEditingTier(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTier(null);
  };

  const isDisabled = saveButtonBarState === "loading";
  const isSaveDisabled = !hasChanges || isDisabled;

  if (initialLoading) {
    return <div>Loading...</div>;
  }

  return (
    <DetailPageLayout gridTemplateColumns={1}>
      <TopNav href={ExtensionsPaths.installedExtensions} title="Tiered Pricing Configuration" />

      <DetailPageLayout.Content>
        <Box paddingX={8} paddingY={6} paddingTop={9}>
          <Box
            as="h1"
            style={{ fontSize: "24px", fontWeight: 500, color: "#202124", marginBottom: "8px" }}
          >
            Tiered Pricing Configuration
          </Box>
          <Box
            as="div"
            style={{ fontSize: "14px", color: "#5f6368", marginBottom: "24px", lineHeight: "1.5" }}
          >
            Configure price tiers to offer discounts based on order quantity. Customers who meet the
            minimum quantity threshold will automatically receive the configured discount.
          </Box>

          {/* Enable / Disable toggle card */}
          <Box
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              marginBottom: "16px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <input
              type="checkbox"
              id="tiered-pricing-enabled"
              checked={formData.isEnabled}
              onChange={e => updateFormData({ isEnabled: e.target.checked })}
              disabled={isDisabled || formData.isEnabled}
              style={{
                width: "18px",
                height: "18px",
                cursor: formData.isEnabled ? "not-allowed" : "pointer",
              }}
            />
            <Box>
              <Text
                as="span"
                htmlFor="tiered-pricing-enabled"
                fontWeight="bold"
                style={{ cursor: formData.isEnabled ? "not-allowed" : "pointer", display: "block" }}
              >
                Enable Tiered Pricing
              </Text>
              <Text size={3} color="default2">
                {formData.isEnabled
                  ? "Tiered pricing is enabled and cannot be disabled."
                  : "When enabled, configured tiers will be applied at checkout."}
              </Text>
            </Box>
          </Box>
        </Box>
      </DetailPageLayout.Content>

      <Savebar>
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => window.history.back()} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={handleSubmit}
          disabled={isSaveDisabled}
        />
      </Savebar>

      <TieredPricingModal
        open={showModal}
        onClose={handleCloseModal}
        onSave={handleModalSave}
        loading={isDisabled}
        editingTier={editingTier}
      />
    </DetailPageLayout>
  );
};

export default TieredPricingView;
